export const documentationContent = {
  title: "Bolt Nexus",
  subtitle: "Multi-Tenant Visibility, Billing & Notifications Platform",
  version: "3.0",
  date: "February 2026",
  
  sections: [
    {
      id: "executive-summary",
      title: "1. Executive Summary",
      icon: "FileText",
      description: "Platform overview and business model",
      content: `
## What is Bolt Nexus?

**Bolt Nexus** is a multi-tenant **visibility, billing, and notifications platform** for Caribbean courier businesses. We integrate with Tasoko warehouse operations and provide couriers with:

- Real-time package tracking
- Automated invoicing to customers
- Multi-channel notifications (WhatsApp, Email, SMS, Push)
- White-label customer portals

## Platform Architecture (3 Stages)

### Stage 1: Florida Warehouse (Tasoko-Managed)
- Intake packages
- Manifest creation
- Tally sheet generation
- Bag building
- API/File export to KP

### Stage 2: KP Core Operations (Bolt Nexus Brain)
- Parse manifest/tally data
- Prefix sorting + KPL Mix splitting
- Invoice generation (bags + singles)
- WhatsApp/Email notifications
- Profitability reporting

### Stage 3: Courier Management (Multi-Tenant)
- 1000+ courier dashboards
- White-label branding
- Role-based access
- Customer portals
- Template websites

## Key Capabilities

| Capability | Description |
|------------|-------------|
| **Multi-Tenant** | 1000+ couriers on single platform |
| **White-Label** | Custom branding, domains, templates |
| **Prefix Sorting** | Auto-identify courier by tracking prefix |
| **KPL Mix Split** | Weight-proportional bag fee allocation |
| **Automated Billing** | Invoice generation per shipment |
| **Profitability** | Per-manifest P&L reporting |
| **Notifications** | WhatsApp, Email, SMS per tenant |
`
    },
    {
      id: "kp-logistics",
      title: "2. KP Logistics Integration",
      icon: "Truck",
      description: "Prefix-based sorting, KPL Mix, and fee structure",
      subsections: [
        {
          id: "data-integration",
          title: "2.1 Data Integration & Identification",
          content: `
## Data Inputs

The system must support multiple data input methods:

### File Upload (Current)
| Format | Use Case |
|--------|----------|
| CSV | Tally sheets, manifest exports |
| XLSX | Detailed cargo lists |
| PDF | Scanned customs documents |

### API Sync (Future-Ready)
- POST /imports/tally - Upload + parse tally
- POST /imports/manifest - Upload + parse manifest
- POST /sync/tasoko/webhook - Direct Tasoko webhook
- POST /shipments/{id}/recalculate

## Canonical Entities

Every import must extract these core entities:

| Entity | Key Fields |
|--------|------------|
| **Shipment** | manifest_no, tally_no, collection_no, awb_no |
| **Bag** | bag_code, bag_type (DEDICATED/MIX), weight_lb |
| **Single** | tracking_no, weight_lb, tenant_id |
| **Bag Item** | tracking_no, item_weight_lb, detected_prefix |
| **Courier/Tenant** | name, prefix_list[], billing_mode |
`
        },
        {
          id: "prefix-sorting",
          title: "2.2 Prefix-Based Sorting",
          content: `
## Dedicated Bag Detection

A bag is automatically classified as **DEDICATED** when:

1. **Bag code contains known prefix**
   - Example: ABC-BAG-001 → Assigned to courier with prefix "ABC"

2. **Majority rule** (configurable threshold)
   - If ≥80% of items in bag match same prefix → Mark as DEDICATED
   - Assign to majority courier

3. **Manual override**
   - Admin can flip bag type: MIX ↔ DEDICATED

## Prefix Sorting Rules

| Bag Code Pattern | Assignment |
|------------------|------------|
| FS-BAG-XXX | FastShip Jamaica |
| SC-BAG-XXX | SpeedyCourier |
| IE-BAG-XXX | IslandExpress |
| KPL-MIX-XXX | Shared bag (split required) |

## Algorithm Overview

1. Extract tracking prefix from each item
2. Match prefix to tenant prefix_list
3. If bag code contains known prefix → DEDICATED
4. Else if ≥80% items match same prefix → DEDICATED
5. Else → MIX (requires splitting)
`
        },
        {
          id: "kpl-mix-logic",
          title: "2.3 KPL Mix Bag Splitting",
          content: `
## What is KPL Mix?

**KPL Mix** bags contain packages from multiple couriers consolidated into a single bag. These require splitting so each courier receives their own invoice for their portion.

## Splitting Algorithm (Weight-Proportional)

The recommended method is **weight-proportional splitting** with fallback to item count.

### Step 1: Calculate Weight Shares

For each KPL Mix bag:
1. Group items by detected tenant
2. Sum weights per tenant
3. Calculate share percentage

### Step 2: Allocate Bag Fees

| Fee Type | Allocation Method |
|----------|-------------------|
| Service Contribution | share_pct × JMD 1,000 |
| Pallet Fee | share_pct × JMD 1,296 |

### Example Calculation

Mix Bag Total Weight: 50 lb

- Courier A items: 20 lb → 40% share
- Courier B items: 30 lb → 60% share

**Service Contribution (JMD 1,000 per bag):**
- Courier A: 1,000 × 0.40 = JMD 400
- Courier B: 1,000 × 0.60 = JMD 600

**Pallet Fee (JMD 1,296 per bag):**
- Courier A: 1,296 × 0.40 = JMD 518.40
- Courier B: 1,296 × 0.60 = JMD 777.60

## Audit Trail

All allocations are stored with:
- **method**: WEIGHT, COUNT, or MANUAL
- **share_pct**: Percentage allocated
- **allocated_weight_lb**: Weight attributed
- **created_at**: Timestamp
- **notes**: Admin comments (if manual override)
`
        },
        {
          id: "fee-structure",
          title: "2.4 Standardized Fee Structure",
          content: `
## Invoice Line Items

Each courier invoice automatically computes these line items:

### Fee Schedule

| Fee Type | Bag Rate (JMD) | Single Rate (JMD) |
|----------|----------------|-------------------|
| Service Contribution | 1,000 | 1,500 |
| Pallet Fee | 1,296 | 648 |
| Delivery Fee | 1,000 (flat per shipment) | - |

### Freight Charge Formula

**Freight (JMD) = (Weight_lb ÷ 2.2) × 2.78 × 165**

Breakdown:
- Convert lb to kg: ÷ 2.2
- Rate per kg: × 2.78 USD
- USD to JMD exchange: × 165

### Invoice Calculation Example

**Courier:** FastShip Jamaica
**Shipment:** MAN-2026-02-28-001

**Items:**
- 4 dedicated bags (total 85 lb)
- 12 singles (total 28 lb)

| Description | Qty | Rate | Amount |
|-------------|-----|------|--------|
| Service Contrib (Bags) | 4 | 1,000 | 4,000 |
| Service Contrib (Singles) | 12 | 1,500 | 18,000 |
| Pallet Fee (Bags) | 4 | 1,296 | 5,184 |
| Pallet Fee (Singles) | 12 | 648 | 7,776 |
| Delivery Fee | 1 | 1,000 | 1,000 |
| Freight (113 lb) | - | - | 23,558.45 |
| **TOTAL** | | | **59,518.45** |

### Configurable Settings

All rates are configurable per tenant in Settings:
- Service Contribution rates
- Pallet Fee rates
- Delivery Fee
- Freight rate per kg
- Exchange rate (USD→JMD)
`
        },
        {
          id: "admin-adjustments",
          title: "2.5 Administrative Adjustments",
          content: `
## Post-Import Adjustments

Administrators can modify the following after import:

### Adjustable Fields

| Field | Description |
|-------|-------------|
| Bag weight | Correct weighing errors |
| Single weight | Update item weights |
| Bag classification | Switch MIX ↔ DEDICATED |
| Prefix mapping | Override detected courier |
| Shipment metadata | Fix manifest/collection numbers |

### Audit Trail (Required)

Every change must record:
- **field**: Which field changed
- **original_value**: Previous value
- **new_value**: Updated value
- **changed_by**: User email
- **changed_at**: Timestamp
- **reason**: Admin comment

## Manual Line Items

Before invoice finalization, admins can add:

| Line Type | Example |
|-----------|---------|
| Handling | Extra handling fees |
| Customs Processing | CPF charges |
| Storage | Warehouse storage fees |
| Discount | Promotional discount |
| Adjustment | Correction entry |

## Invoice States

**Flow:** draft → review → approved → sent → paid (or void)

**Rule:** Once APPROVED, only credit notes can modify totals.
`
        },
        {
          id: "profitability",
          title: "2.6 Profitability Reporting",
          content: `
## Per-Shipment P&L Report

For every manifest/tally, generate a profit & loss report.

### Report Structure

**KP COSTS (What We Paid)**
- Customs Duty
- GCT
- Customs Processing Fee
- Freight (Airline)
- Warehouse Handling
- **TOTAL COST**

**REVENUE (What Couriers Pay)**
- FastShip Jamaica (INV-4521)
- SpeedyCourier (INV-4522)
- IslandExpress (INV-4523)
- KP Direct (INV-4524)
- **TOTAL REVENUE**

**NET PROFIT = Revenue - Cost**
**MARGIN = Profit / Revenue × 100%**

### Drill-Down Dimensions

Reports can be filtered by:
- By Bag (dedicated vs mix)
- By Courier
- By Singles
- By Fee Type (service, pallet, delivery, freight)

### Export Formats

| Format | Use Case |
|--------|----------|
| PDF Summary | Management review |
| CSV Detailed | Accounting import |
| Excel | Pivot table analysis |
`
        }
      ]
    },
    {
      id: "multi-tenant",
      title: "3. Multi-Tenant Architecture",
      icon: "Users",
      description: "1000 couriers on single platform",
      subsections: [
        {
          id: "tenant-model",
          title: "3.1 Tenant Model",
          content: `
## Single Dashboard, Multi-Tenant

One codebase serves all 1000+ couriers with complete data isolation.

### Tenant Isolation Layers

**Application Layer:**
- Resolve Tenant by subdomain: fastship.kplcourier.com → tenant_id
- Custom domain: portal.fastship.com → tenant_id
- Auth token: JWT contains tenant_id

**API Layer:**
- Middleware: Inject tenant_id into all queries
- Validation: Reject cross-tenant access attempts

**Database Layer:**
- Row Level Security (RLS)
- Every table has tenant_id
- Policies enforce: user can only see own tenant
- KP admin bypasses via service role

### What Each Tenant Gets

| Feature | Description |
|---------|-------------|
| Dashboard | Full ops dashboard (packages, invoices, reports) |
| Users | Unlimited team members with roles |
| Customers | Their customer database |
| Invoices | Billing for their customers |
| Notifications | Branded WhatsApp/Email |
| Website | Template or custom portal |
| API Access | Tenant-scoped API keys |
`
        },
        {
          id: "roles-permissions",
          title: "3.2 Roles & Permissions",
          content: `
## Tenant Roles

Each courier can manage their team with these roles:

| Role | Permissions |
|------|-------------|
| **Owner** | Full access, billing, delete tenant |
| **Admin** | All ops, user management, settings |
| **Cashier** | Invoices, payments, statements |
| **Warehouse Ops** | Packages, bags, tracking (view/limited edit) |
| **Driver** | POD, delivery confirmation, signatures |
| **Support** | Customer lookup, tracking, read-only |

### Permission Matrix

| Action | Owner | Admin | Cashier | Ops | Driver | Support |
|--------|-------|-------|---------|-----|--------|---------|
| View Packages | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ |
| Edit Packages | ✓ | ✓ | ✗ | ✓ | ✗ | ✗ |
| View Invoices | ✓ | ✓ | ✓ | ✓ | ✗ | ✓ |
| Create Invoices | ✓ | ✓ | ✓ | ✗ | ✗ | ✗ |
| Approve Invoices | ✓ | ✓ | ✗ | ✗ | ✗ | ✗ |
| Manage Users | ✓ | ✓ | ✗ | ✗ | ✗ | ✗ |
| Branding Settings | ✓ | ✓ | ✗ | ✗ | ✗ | ✗ |
| Billing Settings | ✓ | ✗ | ✗ | ✗ | ✗ | ✗ |

## KP Super Admin

KP administrators have a separate layer:

| Capability | Description |
|------------|-------------|
| Create/Suspend Tenants | Onboard new couriers |
| Set Global Pricing | Default fee structures |
| Cross-Tenant Reports | Profitability across all tenants |
| Audit Logs | View all system activity |
| Import Operations | Process Tasoko manifests |
`
        },
        {
          id: "white-label",
          title: "3.3 White-Label System",
          content: `
## Branding Options

Each tenant can customize:

### Visual Branding

| Element | Options |
|---------|---------|
| Logo | Light + dark versions |
| Colors | Primary, secondary, accent |
| Favicon | Custom icon |
| Company Name | Header + footer |
| Tagline | Subtitle text |

### Document Templates

| Template | Customizable |
|----------|--------------|
| Invoice PDF | Header, footer, logo, terms |
| Receipt | Branded receipt |
| Statement | Monthly statement |
| Email | Header, signature, colors |
| WhatsApp | Message templates |

### Domain Options

**Option A: Subdomain (Default)**
- https://fastship.kplcourier.com
- https://speedycourier.kplcourier.com

**Option B: Custom Domain (CNAME)**
- https://portal.fastship.com
- https://track.speedycourier.jm

### Tenant Branding Settings

- logo_light_url: URL to light logo
- logo_dark_url: URL to dark logo
- primary_color: Hex code (e.g., #3b82f6)
- secondary_color: Hex code
- company_name: Display name
- tagline: Company tagline
- domain_type: subdomain or custom_domain
- custom_domain: Full domain if custom
`
        },
        {
          id: "tenant-website",
          title: "3.4 Tenant Website Builder",
          content: `
## Website Options

Each tenant can deploy a branded website:

### Option A: Template Website (Default)

Pick from prebuilt templates:

| Page | Features |
|------|----------|
| Home/Landing | Hero, features, CTA |
| Rates | Pricing table, calculator |
| How It Works | Process steps |
| Track | Package tracking lookup |
| Sign Up | Customer registration |
| Login | Customer portal access |
| Contact | Form, WhatsApp click-to-chat |

### Template Customization

| Setting | Control |
|---------|---------|
| Layout | Select template ID |
| Theme | Colors, fonts |
| Content | Hero text, images |
| Pages | Enable/disable pages |
| Sections | Reorder, hide sections |
| Policies | Terms, privacy text |

### Option B: Custom Portal (Enterprise)

For larger couriers:
- Custom front-end design
- Same backend APIs
- Full brand control
- Separate hosting option

### Website ↔ Dashboard Sync

| Customer Action | Dashboard Effect |
|-----------------|------------------|
| Sign up | New customer created |
| Create order | Order in dashboard |
| Upload receipt | Attached to shipment |
| Pay invoice | Payment recorded |
| Track package | Activity logged |
`
        }
      ]
    },
    {
      id: "database-schema",
      title: "4. Database Schema",
      icon: "Database",
      description: "PostgreSQL + Supabase + RLS",
      subsections: [
        {
          id: "schema-overview",
          title: "4.1 Schema Overview",
          content: `
## Database Architecture

**Technology:** PostgreSQL + Supabase + Row Level Security (RLS)

**Pattern:** Single database, shared tables, tenant_id isolation

### Why This Approach?

| Benefit | Description |
|---------|-------------|
| **Scalability** | 1000+ tenants on one DB |
| **Maintainability** | One schema to migrate |
| **Cost** | No 1000 separate DBs |
| **Reporting** | Easy cross-tenant KP reports |
| **Security** | RLS enforces isolation at DB layer |

### Table Groups

**GROUP 1: IDENTITY + TENANCY**
- tenants
- tenant_users

**GROUP 2: KP GLOBAL OPERATIONS**
- shipments
- bags
- bag_items
- singles

**GROUP 3: MIX ALLOCATION BRIDGE**
- mix_allocations

**GROUP 4: BILLING**
- invoices
- invoice_lines

**GROUP 5: CUSTOMERS & NOTIFICATIONS**
- customers
- notifications
`
        },
        {
          id: "core-tables",
          title: "4.2 Core Tables",
          content: `
## Group 1: Identity + Tenancy

### tenants
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| name | VARCHAR(255) | Courier name |
| slug | VARCHAR(100) | For subdomains |
| prefixes | TEXT[] | Tracking prefixes |
| branding | JSONB | Logo, colors, etc. |
| billing_mode | VARCHAR(50) | KP_BILLS_COURIER or COURIER_BILLS_CUSTOMER |
| status | VARCHAR(20) | active, suspended |
| created_at | TIMESTAMPTZ | Created timestamp |

### tenant_users
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| tenant_id | UUID | FK to tenants |
| user_id | UUID | FK to auth.users |
| role | VARCHAR(50) | owner, admin, cashier, ops, driver, support |
| status | VARCHAR(20) | active, invited, disabled |

## Group 2: KP Global Operations

### shipments
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| manifest_no | VARCHAR(100) | Manifest number |
| tally_no | VARCHAR(100) | Tally sheet number |
| collection_no | VARCHAR(100) | Collection number |
| awb_no | VARCHAR(100) | Air waybill number |
| status | VARCHAR(50) | pending, processed, invoiced |
| total_bags | INT | Bag count |
| total_singles | INT | Singles count |
| total_weight_lb | NUMERIC | Total weight |
| arrived_at | TIMESTAMPTZ | Arrival date |

### bags
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| shipment_id | UUID | FK to shipments |
| bag_code | VARCHAR(100) | Bag identifier |
| bag_type | VARCHAR(20) | DEDICATED or MIX |
| weight_lb | NUMERIC | Bag weight |
| owner_tenant_id | UUID | FK to tenants (NULL for MIX) |
| item_count | INT | Items in bag |

### bag_items
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| bag_id | UUID | FK to bags |
| tracking_no | VARCHAR(100) | Tracking number |
| item_weight_lb | NUMERIC | Item weight |
| detected_prefix | VARCHAR(20) | Detected prefix |
| detected_tenant_id | UUID | Matched tenant |

### singles
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| shipment_id | UUID | FK to shipments |
| tracking_no | VARCHAR(100) | Tracking number |
| weight_lb | NUMERIC | Item weight |
| tenant_id | UUID | FK to tenants |
| detected_prefix | VARCHAR(20) | Prefix used |
`
        },
        {
          id: "allocation-billing",
          title: "4.3 Allocation & Billing Tables",
          content: `
## Group 3: Mix Allocation Bridge

### mix_allocations

This is the **critical table** for KPL Mix bag splitting:

| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| bag_id | UUID | FK to bags |
| tenant_id | UUID | FK to tenants |
| method | VARCHAR(20) | WEIGHT, COUNT, MANUAL |
| share_pct | NUMERIC(5,4) | e.g., 0.4000 = 40% |
| allocated_weight_lb | NUMERIC | Weight attributed |
| allocated_service_fee_jmd | NUMERIC | Service fee portion |
| allocated_pallet_fee_jmd | NUMERIC | Pallet fee portion |
| notes | TEXT | Admin comments |
| created_by | UUID | User who created |
| created_at | TIMESTAMPTZ | Timestamp |

## Group 4: Billing

### invoices
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| tenant_id | UUID | FK to tenants |
| shipment_id | UUID | FK to shipments |
| invoice_no | VARCHAR(50) | Invoice number |
| status | VARCHAR(20) | draft, review, approved, sent, paid, void |
| service_total_jmd | NUMERIC | Service fees total |
| pallet_total_jmd | NUMERIC | Pallet fees total |
| delivery_fee_jmd | NUMERIC | Delivery fee |
| freight_total_jmd | NUMERIC | Freight total |
| manual_total_jmd | NUMERIC | Manual adjustments |
| subtotal_jmd | NUMERIC | Subtotal |
| tax_jmd | NUMERIC | Tax amount |
| total_jmd | NUMERIC | Grand total |
| due_date | DATE | Payment due |
| sent_at | TIMESTAMPTZ | When sent |
| paid_at | TIMESTAMPTZ | When paid |

### invoice_lines
| Column | Type | Description |
|--------|------|-------------|
| id | UUID | Primary key |
| invoice_id | UUID | FK to invoices |
| tenant_id | UUID | Denormalized for RLS |
| line_type | VARCHAR(50) | SERVICE_BAG, PALLET_SINGLE, etc. |
| description | VARCHAR(255) | Line description |
| qty | NUMERIC | Quantity |
| rate_jmd | NUMERIC | Rate |
| amount_jmd | NUMERIC | Line total |
| taxable | BOOLEAN | Is taxable |
| meta | JSONB | References (bag_id, etc.) |
`
        },
        {
          id: "rls-policies",
          title: "4.4 Row Level Security",
          content: `
## RLS Strategy

### Key Principle

**Never trust the app alone. Enforce tenant isolation at DB layer.**

### How It Works

1. Every table with tenant data has tenant_id column
2. RLS policies check: user can only access rows where tenant_id matches their membership
3. KP admin bypasses using service role key

### Tables with RLS Enabled

- invoices
- invoice_lines
- singles
- mix_allocations
- customers
- notifications

### Tables WITHOUT RLS (KP Global)

- shipments (KP admin manages)
- bags (KP admin manages)
- bag_items (contains multi-tenant data, use views)

## Tenant-Safe Views

For tables like bag_items that contain multi-tenant data, use views:

### tenant_bags_view

Shows only:
- Dedicated bags owned by tenant
- Mix bag allocations for tenant

Columns exposed:
- bag_id, shipment_id, bag_code, bag_type
- tenant_id (resolved)
- share_pct, allocated_weight_lb

## KP Admin Access

KP administrators bypass RLS using service role key for:
- Cross-tenant profitability reports
- Global shipment management
- Audit logging
`
        }
      ]
    },
    {
      id: "api-endpoints",
      title: "5. API Endpoints",
      icon: "Server",
      description: "RESTful API specification",
      content: `
## API Design

All endpoints are designed for Tasoko integration readiness.

### Import Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| POST | /api/imports/tally | Upload + parse tally sheet |
| POST | /api/imports/manifest | Upload + parse manifest |
| POST | /api/sync/tasoko/webhook | Future Tasoko direct sync |

### Shipment Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | /api/shipments | List all shipments |
| GET | /api/shipments/{id} | Get shipment details |
| POST | /api/shipments/{id}/recalculate | Recalculate invoices |
| GET | /api/shipments/{id}/profitability | Get P&L report |

### Invoice Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | /api/invoices | List invoices (tenant-filtered) |
| GET | /api/invoices/{id} | Get invoice details |
| POST | /api/invoices | Create draft invoice |
| PATCH | /api/invoices/{id} | Update invoice |
| POST | /api/invoices/{id}/approve | Approve invoice |
| POST | /api/invoices/{id}/send | Send to courier |
| POST | /api/invoices/{id}/void | Void with credit note |

### Notification Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| POST | /api/notifications/send | Send notification |
| POST | /api/notifications/whatsapp | Send WhatsApp |
| POST | /api/notifications/email | Send email |

### Tenant Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | /api/tenants | List tenants (KP admin) |
| POST | /api/tenants | Create tenant |
| PATCH | /api/tenants/{id} | Update tenant |
| GET | /api/tenants/{id}/users | List tenant users |
| POST | /api/tenants/{id}/users | Add user to tenant |
`
    },
    {
      id: "implementation-plan",
      title: "6. Implementation Roadmap",
      icon: "Rocket",
      description: "Phased development plan",
      content: `
## Development Phases

### Phase 1: Foundation (Week 1-2)

**Database Setup**
- [ ] Set up Supabase project
- [ ] Create core tables (tenants, shipments, bags, invoices)
- [ ] Implement RLS policies
- [ ] Create tenant-safe views

**Core API**
- [ ] Authentication with Supabase Auth
- [ ] Tenant resolution middleware
- [ ] CRUD endpoints for shipments, bags, invoices
- [ ] Import endpoint for tally/manifest files

**KPL Mix Algorithm**
- [ ] Prefix detection function
- [ ] Bag classification logic (DEDICATED/MIX)
- [ ] Weight-proportional allocation
- [ ] Invoice line generation

### Phase 2: Tenant System (Week 3-4)

**Multi-Tenant Dashboard**
- [ ] Tenant-aware React context
- [ ] Role-based route protection
- [ ] Tenant data isolation in UI

**User Management**
- [ ] Tenant user CRUD
- [ ] Role assignment
- [ ] Invite flow

**Branding Settings**
- [ ] Logo upload
- [ ] Color customization
- [ ] Domain configuration

### Phase 3: White-Label (Week 5-6)

**Subdomain Routing**
- [ ] Tenant resolution by subdomain
- [ ] Dynamic branding injection
- [ ] SSL certificate handling

**Document Templates**
- [ ] Invoice PDF generation
- [ ] Email templates
- [ ] WhatsApp templates

### Phase 4: Customer Portal (Week 7-8)

**Template Website Builder**
- [ ] Template selection UI
- [ ] Content editor
- [ ] Page configuration

**Customer Features**
- [ ] Tracking page
- [ ] Customer login
- [ ] Invoice viewing
- [ ] Receipt upload

### Phase 5: Notifications (Week 9)

**Multi-Channel**
- [ ] WhatsApp Business API
- [ ] SendGrid email
- [ ] SMS (Twilio)

**Automation**
- [ ] Invoice sent triggers
- [ ] Payment confirmation
- [ ] Package ready alerts

### Phase 6: Production (Week 10)

**Testing & QA**
- [ ] Multi-tenant security audit
- [ ] Load testing (1000 tenants)
- [ ] RLS policy verification

**Deployment**
- [ ] Production Supabase
- [ ] CDN for static assets
- [ ] Monitoring & alerts
`
    },
    {
      id: "glossary",
      title: "7. Glossary",
      icon: "BookOpen",
      description: "Key terms and definitions",
      content: `
## Terms & Definitions

| Term | Definition |
|------|------------|
| **Tenant** | A courier company using the platform |
| **Prefix** | Tracking number prefix identifying a courier (e.g., FS, SC) |
| **Shipment** | A collection of bags/items arriving on one manifest |
| **Manifest** | Customs declaration document for cargo |
| **Tally Sheet** | Itemized list of packages in a shipment |
| **Dedicated Bag** | Bag owned by single courier |
| **KPL Mix Bag** | Shared bag with items from multiple couriers |
| **Mix Allocation** | Record of courier's share in a mix bag |
| **Singles** | Individual items not in a dedicated bag |
| **RLS** | Row Level Security - database access control |
| **White-Label** | Customized branding per tenant |
| **AWB** | Air Waybill - airline cargo tracking number |

## Abbreviations

| Abbrev | Full Form |
|--------|-----------|
| KP | KP Logistics (platform operator) |
| JMD | Jamaican Dollar |
| GCT | General Consumption Tax |
| CPF | Customs Processing Fee |
| POD | Proof of Delivery |
| RLS | Row Level Security |
| UUID | Universally Unique Identifier |
`
    }
  ]
};

export default documentationContent;
